import React, { useEffect, useState } from 'react';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

export interface CustomerAuditEntry {
  id: string;
  timestamp: string;
  action: string;
  actor: { id: string | null; name: string; email: string | null };
  details: any;
}

interface Props {
  isOpen: boolean;
  customerId: string | null;
  customerName?: string;
  onClose: () => void;
}

const ACTION_LABELS: Record<string, string> = {
  customer_update: 'Updated customer',
  customer_delete: 'Deleted customer',
  customer_transfer: 'Transferred ownership',
  customer_share_create: 'Shared customer',
  customer_share_update: 'Changed share permission',
  customer_share_revoke: 'Revoked share',
  report_delete: 'Deleted report',
};

function formatChanges(details: any): React.ReactNode {
  if (!details) return null;
  if (details.changed && typeof details.changed === 'object') {
    const entries = Object.entries(details.changed) as Array<[string, { from: any; to: any }]>;
    if (entries.length === 0) return null;
    return (
      <ul className="mt-1 ml-2 text-xs text-gray-600 dark:text-gray-400 space-y-0.5">
        {entries.map(([field, change]) => (
          <li key={field}>
            <span className="font-medium">{field}</span>: <span className="line-through">{String(change.from ?? '∅')}</span>
            {' → '}
            <span>{String(change.to ?? '∅')}</span>
          </li>
        ))}
      </ul>
    );
  }
  if (details.shared && Array.isArray(details.shared)) {
    return (
      <ul className="mt-1 ml-2 text-xs text-gray-600 dark:text-gray-400">
        {details.shared.map((s: any, i: number) => (
          <li key={i}>
            with {s.name || s.id} as <span className="font-medium">{s.permission || 'viewer'}</span>
          </li>
        ))}
      </ul>
    );
  }
  if (details.shareId && details.permission) {
    return (
      <p className="mt-1 ml-2 text-xs text-gray-600 dark:text-gray-400">
        New permission: <span className="font-medium">{details.permission}</span>
      </p>
    );
  }
  if (details.toUserName) {
    return (
      <p className="mt-1 ml-2 text-xs text-gray-600 dark:text-gray-400">
        New owner: <span className="font-medium">{details.toUserName}</span>
      </p>
    );
  }
  if (details.reportTitle) {
    return (
      <p className="mt-1 ml-2 text-xs text-gray-600 dark:text-gray-400">
        Report: <span className="font-medium">{details.reportTitle}</span>
      </p>
    );
  }
  return null;
}

const CustomerAuditModal: React.FC<Props> = ({ isOpen, customerId, customerName, onClose }) => {
  const [entries, setEntries] = useState<CustomerAuditEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen || !customerId) return;
    let cancelled = false;
    setLoading(true);
    setError('');
    const token = localStorage.getItem('riskninja-token');
    fetch(`${API_BASE_URL}/api/customers/${customerId}/audit-log`, {
      headers: { 'Authorization': `Bearer ${token}` },
    })
      .then((r) => {
        if (!r.ok) throw new Error('Failed to load audit log');
        return r.json();
      })
      .then((data) => { if (!cancelled) setEntries(data.entries || []); })
      .catch((err) => { if (!cancelled) setError(err.message || 'Failed to load'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [isOpen, customerId]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-black/50 backdrop-blur-sm" onClick={onClose} />
        <div className="inline-block w-full max-w-2xl text-left align-middle transition-all transform bg-white dark:bg-gray-800 rounded-lg shadow-xl my-8">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Customer Change Log</h3>
              {customerName && <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{customerName}</p>}
            </div>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>

          <div className="px-6 py-4 max-h-[65vh] overflow-y-auto">
            {loading && <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-8">Loading…</p>}
            {error && <p className="text-sm text-red-500 text-center py-4">{error}</p>}
            {!loading && !error && entries.length === 0 && (
              <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-8">No changes recorded yet.</p>
            )}
            {!loading && entries.length > 0 && (
              <ul className="space-y-3">
                {entries.map((e) => (
                  <li key={e.id} className="flex gap-3 pb-3 border-b border-gray-100 dark:border-gray-700 last:border-0">
                    <div className="flex-shrink-0 w-2 h-2 mt-2 rounded-full bg-blue-500" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline justify-between gap-2 flex-wrap">
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {ACTION_LABELS[e.action] || e.action}
                        </p>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {new Date(e.timestamp).toLocaleString()}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        by {e.actor.name}
                        {e.actor.email && <span className="ml-1">({e.actor.email})</span>}
                      </p>
                      {formatChanges(e.details)}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerAuditModal;
